import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { auth, db } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc, collection, getDocs, serverTimestamp } from "firebase/firestore";
import { useSoloTimetableStore, DAYS, PERIODS } from "../../lib/stores/soloTimetableStore";
import { useLearningProfileStore } from "../../lib/stores/learningProfileStore";
import SoloTimetableGrid from "../../components/solo/SoloTimetableGrid";
import SoloActivityPicker from "../../components/solo/SoloActivityPicker";
import { exportSoloTimetableToPDF } from "../../utils/exportSoloTimetablePDF";
import { Icons } from "../../lib/Icons";

const SoloTimetableBuilder = () => {
  const navigate = useNavigate();
  const { profileId } = useParams();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);

  const {
    timetableName,
    setTimetableName,
    grid,
    settings,
    updateSettings,
    toggleTransitions,
    hasUnsavedChanges,
    isSaving,
    setSaving,
    markAsSaved,
    currentTimetableId,
    initializeFromProfile,
    loadTimetable,
    setAvailableActivities,
    autoPopulate,
    clearGrid,
    reset,
    editingSlot,
    setEditingSlot,
    clearEditingSlot,
    getTimetableData,
    getActivityCount
  } = useSoloTimetableStore();

  const { recommendedActivities, profileName } = useLearningProfileStore();

  // Extract unique activity titles from the grid for symbol generation
  const getActivityTitlesForSymbols = () => {
    const titles = new Set();
    DAYS.forEach(day => {
      PERIODS.filter(p => p.type === 'activity').forEach(period => {
        const activity = grid[day]?.[period.key]?.activity;
        if (activity?.title && !activity.isTransition) {
          titles.add(activity.title);
        }
      });
    });
    return Array.from(titles);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate('/login');
        return;
      }
      setUser(currentUser);

      // Check if we're coming from learning profile results
      const fromProfile = location.state?.fromProfile;

      if (fromProfile && recommendedActivities.length > 0) {
        // Initialize from profile
        initializeFromProfile(profileId, profileName || 'Learning Profile', recommendedActivities);
      } else if (profileId && profileId !== 'new') {
        // Try to load existing timetable
        try {
          const timetableDoc = await getDoc(doc(db, 'soloTimetables', profileId));
          if (timetableDoc.exists()) {
            loadTimetable({ id: timetableDoc.id, ...timetableDoc.data() });
          } else {
            // Try loading from a learning profile
            const profileDoc = await getDoc(doc(db, 'learningProfiles', profileId));
            if (profileDoc.exists()) {
              const profileData = profileDoc.data();
              // Load KB items for this profile
              const kbSnapshot = await getDocs(collection(db, 'kb_items'));
              const kbItems = kbSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
              
              // Use the profile's recommended activity IDs if available
              const activityIds = profileData.recommendedActivityIds || [];
              const activities = activityIds.map(id => kbItems.find(item => item.id === id)).filter(Boolean);
              
              if (activities.length > 0) {
                initializeFromProfile(profileId, profileData.name || 'Learning Profile', activities);
              } else {
                // No activities, just set up empty
                reset();
                setTimetableName(profileData.name ? `${profileData.name} - Timetable` : 'New Timetable');
              }
            } else {
              reset();
            }
          }
        } catch (err) {
          console.error('Error loading timetable:', err);
          reset();
        }
      } else {
        // New timetable
        reset();
        setTimetableName('New Weekly Timetable');
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, profileId, location.state, recommendedActivities]);

  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    try {
      const timetableData = getTimetableData();
      timetableData.userId = user.uid;

      if (currentTimetableId) {
        // Update existing
        await updateDoc(doc(db, 'soloTimetables', currentTimetableId), {
          ...timetableData,
          updatedAt: serverTimestamp()
        });
        markAsSaved(currentTimetableId);
      } else {
        // Create new
        const newDocRef = doc(collection(db, 'soloTimetables'));
        await setDoc(newDocRef, {
          ...timetableData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        markAsSaved(newDocRef.id);
      }
    } catch (err) {
      console.error('Error saving timetable:', err);
    }
    setSaving(false);
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      await exportSoloTimetableToPDF(grid, timetableName, settings);
    } catch (err) {
      console.error('Error exporting PDF:', err);
    }
    setExporting(false);
  };

  const handleAutoPopulate = () => {
    if (window.confirm('This will replace all current activities. Continue?')) {
      autoPopulate();
    }
  };

  const handleClearAll = () => {
    if (window.confirm('This will remove all activities from the timetable. Continue?')) {
      clearGrid();
    }
  };

  const activityCount = getActivityCount();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500/20 border-t-purple-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading timetable...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Link to="/solo" className="flex items-center gap-2">
                <img
                  src="/brand/White logo on purple.png"
                  alt="SEN-Ed"
                  className="w-10 h-10 rounded-xl"
                />
              </Link>
              <div className="flex items-center gap-2">
                <Icons.TableCells className="w-5 h-5 text-purple-400" />
                <span className="text-lg font-semibold text-white">Timetable Builder</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {hasUnsavedChanges && (
                <span className="text-xs text-amber-400 flex items-center gap-1">
                  <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"></span>
                  Unsaved changes
                </span>
              )}
              
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition-colors"
                title="Settings"
              >
                <Icons.Cog className="w-5 h-5" />
              </button>

              <button
                onClick={handleExportPDF}
                disabled={exporting}
                className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-colors disabled:opacity-50"
              >
                <Icons.Download className="w-4 h-4" />
                {exporting ? 'Exporting...' : 'Download PDF'}
              </button>

              <button
                onClick={handleSave}
                disabled={isSaving || !hasUnsavedChanges}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white rounded-xl transition-colors disabled:opacity-50"
              >
                <Icons.Upload className="w-4 h-4" />
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Timetable Name */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            {isEditingName ? (
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={timetableName}
                  onChange={(e) => setTimetableName(e.target.value)}
                  className="text-2xl font-bold bg-slate-800 text-white px-4 py-2 rounded-xl border border-slate-600 focus:border-purple-500 focus:outline-none"
                  autoFocus
                  onBlur={() => setIsEditingName(false)}
                  onKeyDown={(e) => e.key === 'Enter' && setIsEditingName(false)}
                />
              </div>
            ) : (
              <button
                onClick={() => setIsEditingName(true)}
                className="flex items-center gap-2 group"
              >
                <h1 className="text-2xl font-bold text-white">{timetableName || 'Untitled Timetable'}</h1>
                <Icons.PencilSquare className="w-5 h-5 text-slate-500 group-hover:text-slate-300 transition-colors" />
              </button>
            )}
            <p className="text-sm text-slate-400 mt-1">
              {activityCount} activities scheduled • Click on a slot to edit
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleAutoPopulate}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              <Icons.Sparkles className="w-4 h-4" />
              Auto-fill
            </button>
            <button
              onClick={handleClearAll}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              <Icons.XMark className="w-4 h-4" />
              Clear All
            </button>
          </div>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="mb-6 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
            <h3 className="text-sm font-medium text-white mb-4">Timetable Settings</h3>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.includeTransitions}
                  onChange={toggleTransitions}
                  className="w-4 h-4 rounded border-slate-600 text-purple-500 focus:ring-purple-500 focus:ring-offset-slate-800"
                />
                <span className="text-sm text-slate-300">Show transition periods</span>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.showTimes}
                  onChange={() => updateSettings({ showTimes: !settings.showTimes })}
                  className="w-4 h-4 rounded border-slate-600 text-purple-500 focus:ring-purple-500 focus:ring-offset-slate-800"
                />
                <span className="text-sm text-slate-300">Show times</span>
              </label>
            </div>
          </div>
        )}

        {/* Timetable Grid */}
        <div className="bg-slate-800/30 rounded-2xl p-4 border border-slate-700/50">
          <SoloTimetableGrid
            onEditSlot={(day, periodKey) => setEditingSlot(day, periodKey)}
          />
        </div>

        {/* Help Section */}
        <div className="mt-8 p-6 bg-slate-800/30 rounded-2xl border border-slate-700/50">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Icons.LightBulb className="w-5 h-5 text-amber-400" />
            Tips for Using the Timetable
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                <Icons.MusicalNote className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-white">Transition Songs</h4>
                <p className="text-xs text-slate-400 mt-1">
                  Use familiar songs or music to signal activity changes. This helps with predictability and reduces anxiety.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <Icons.Clock className="w-4 h-4 text-green-400" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-white">Consistent Timing</h4>
                <p className="text-xs text-slate-400 mt-1">
                  Try to keep activities at the same time each day. Predictability builds security and reduces stress.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                <Icons.Photo className="w-4 h-4 text-purple-400" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-white">Visual Support</h4>
                <p className="text-xs text-slate-400 mt-1">
                  Print the timetable and add symbol cards. You can use our Symbol Generator to create matching visuals.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                <Icons.Coffee className="w-4 h-4 text-orange-400" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-white">Regular Breaks</h4>
                <p className="text-xs text-slate-400 mt-1">
                  Morning break includes snack time. Use breaks for sensory regulation and movement.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          {profileId && (
            <Link
              to={`/solo/profiles/${profileId}`}
              className="flex items-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-colors"
            >
              <Icons.ChevronLeft className="w-4 h-4" />
              Back to Profile
            </Link>
          )}
          <Link
            to="/solo/symbols"
            state={{ words: getActivityTitlesForSymbols() }}
            className="flex items-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-colors"
          >
            <Icons.Sparkles className="w-4 h-4" />
            Create Symbols
          </Link>
          <Link
            to="/solo"
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white rounded-xl transition-colors"
          >
            <Icons.Home className="w-4 h-4" />
            Back to Dashboard
          </Link>
        </div>
      </main>

      {/* Activity Picker Modal */}
      {editingSlot && (
        <SoloActivityPicker
          isOpen={!!editingSlot}
          onClose={clearEditingSlot}
          day={editingSlot.day}
          periodKey={editingSlot.periodKey}
        />
      )}
    </div>
  );
};

export default SoloTimetableBuilder;
